import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { NotificationState } from 'components/shared/Notification';
import useAIJobChannel from 'hooks/useAIJobChannel';
import {
  getAIJobNotificationTitle,
  isOnJobLoadingPage as isOnJobReturnUrl,
  updateReviewForAIJob,
} from 'lib/aiJobNotificationHelpers';
import { dateAtTimeNoRelative } from 'lib/date';
import { clone } from 'lib/util';
import AIJob from 'models/AIJob';
import {
  type AIJobNotificationContext,
  AIJobNotificationType,
  useAIJobNotificationStore,
} from 'stores/aiJobNotificationStore';
import { useNotificationActions } from 'stores/notificationStore';

type AIJobTrackerProps = {
  context: AIJobNotificationContext;
};

/**
 * Component that manages a single AI job's notification lifecycle
 */
const AIJobTracker = ({ context }: AIJobTrackerProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    untrackAIJob,
    dismissJobNotification,
    addJobNotificationMapping,
    removeJobNotificationMapping,
    dismissedProcessingJobs,
    jobNotificationMap,
    clearDismissedProcessingJob,
  } = useAIJobNotificationStore();
  const { addNotification, updateNotification, removeNotification } = useNotificationActions();

  const isOnReturnUrl = useMemo(() => isOnJobReturnUrl(location.pathname, context), [location.pathname, context]);

  const notificationId = useMemo(() => jobNotificationMap.get(context.jobId), [jobNotificationMap, context.jobId]);

  const wasDismissedWhileProcessing = useMemo(
    () => dismissedProcessingJobs.has(context.jobId),
    [dismissedProcessingJobs, context.jobId]
  );

  const [isJobProcessing, setIsJobProcessing] = useState(true);
  const jobRef = useRef<AIJob>(null);

  // Handle job status updates
  const handleJobUpdate = useCallback(
    (job: AIJob) => {
      jobRef.current = clone(AIJob, job);
      setIsJobProcessing(job.isProcessing);
      updateReviewForAIJob(
        job,
        context.reviewId,
        context.jobType === AIJobNotificationType.REFERRAL_SUMMARY ? 'referral' : 'authorization'
      );

      // Get fresh state from store to avoid stale closure values
      const store = useAIJobNotificationStore.getState();
      const currentNotificationId = store.jobNotificationMap.get(job.id);
      const wasDismissed = store.dismissedProcessingJobs.has(job.id);

      // Job completed or errored
      if (!job.isProcessing) {
        // Recalculate isOnReturnUrl with current location to avoid stale closure
        const currentIsOnReturnUrl = isOnJobReturnUrl(window.location.pathname, context);

        const payload = {
          state: job.isCompleted ? NotificationState.SUCCESS : NotificationState.FAILURE,
          title: getAIJobNotificationTitle(context, job.isCompleted ? 'completed' : 'failed'),
          timestamp: dateAtTimeNoRelative(new Date()) as string,
          onActionClick: () => navigate(context.returnUrl),
          onDismiss: () => handleDismiss(job.id),
          actionSuccessText:
            context.jobType === AIJobNotificationType.AUTHORIZATION_COMPARISON ? 'View comparison' : 'View Summary',
        };

        if (currentIsOnReturnUrl) {
          untrackAIJob(job.id);
        } else if (wasDismissed) {
          // User dismissed the notification while job was processing
          // Re-show the notification now that it's complete
          const newNotificationId = addNotification(payload);

          addJobNotificationMapping(job.id, newNotificationId);
          clearDismissedProcessingJob(job.id);
        } else if (currentNotificationId) {
          // Update existing notification
          updateNotification(currentNotificationId, payload);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [context]
  );

  // Subscribe to job updates via WebSocket
  useAIJobChannel({
    aiJobId: isJobProcessing ? context.jobId : undefined,
    onMessageReceived: (job: AIJob) => {
      handleJobUpdate(job);
    },
  });

  // Create or remove notification based on route
  useEffect(() => {
    if (isOnReturnUrl) {
      // User is on the loading page, remove notification if it exists
      if (notificationId) {
        removeNotification(notificationId);
        if (jobRef.current?.isProcessing ?? true) removeJobNotificationMapping(context.jobId);
        else untrackAIJob(context.jobId);
      }
    } else {
      // User is NOT on loading page, ensure notification exists (if not dismissed)
      if (!notificationId && !wasDismissedWhileProcessing) {
        const newNotificationId = addNotification({
          state: NotificationState.PROCESSING,
          title: getAIJobNotificationTitle(context, 'processing'),
          timestamp: undefined,
          onActionClick: undefined,
          onDismiss: () => handleDismiss(context.jobId),
        });

        addJobNotificationMapping(context.jobId, newNotificationId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnReturnUrl, notificationId, wasDismissedWhileProcessing, context]);

  const handleDismiss = (jobId: string) => {
    dismissJobNotification(jobId, jobRef.current?.isProcessing ?? true);

    const currentNotificationId = jobNotificationMap.get(jobId);
    if (currentNotificationId) {
      removeNotification(currentNotificationId);
      removeJobNotificationMapping(jobId);
    }
  };

  return null; // This component only manages side effects
};

export const AIJobNotificationManager = () => {
  const { trackedJobs } = useAIJobNotificationStore();

  const trackedJobsArray = useMemo(() => Array.from(trackedJobs.values()), [trackedJobs]);

  return (
    <>
      {trackedJobsArray.map((context) => (
        <AIJobTracker key={context.jobId} context={context} />
      ))}
    </>
  );
};
